import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Upload, X, CheckCircle2, Download, Info, RefreshCw, LogOut } from "lucide-react";
import SignatureCanvas from "react-signature-canvas";
import axios from "axios";
import { useAuth } from "./AuthContext";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

const input =
  "w-full rounded-xl bg-white border border-ink-700/10 px-3 py-2.5 text-sm text-ink-900 focus:outline-none focus:ring-2 focus:ring-gold-500";

const Header = () => {
  const { user, logout } = useAuth();
  return (
    <header className="max-w-4xl mx-auto px-6 pt-8 flex items-center justify-between">
      <a href="/Gold" data-testid="id-back-link" className="flex items-center gap-3">
        <img src="/moonshine-logo.png" alt="Moonshine Solutions" className="h-14 w-14 rounded-full object-cover ring-1 ring-gold-500/30" />
        <div className="leading-tight">
          <div className="font-display font-medium text-ink-900 text-[15px]">Moonshine</div>
          <div className="font-sans text-[11px] uppercase tracking-[0.18em] text-ink-500">Solutions</div>
        </div>
      </a>
      <div className="flex items-center gap-4">
        <a href="/Gold" className="inline-flex items-center gap-2 text-sm text-ink-500 hover:text-gold-700 transition">
          <ArrowLeft className="w-4 h-4" /> Zurück zu /Gold
        </a>
        {user && (
          <button onClick={logout} data-testid="id-logout" className="inline-flex items-center gap-1.5 text-sm text-ink-500 hover:text-gold-700 transition">
            <LogOut className="w-4 h-4" /> Abmelden
          </button>
        )}
      </div>
    </header>
  );
};

const ImageBox = ({ label, imageData, onUpload, onClear, testId }) => {
  const fileRef = useRef(null);
  const handleFile = (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      alert("Bild zu groß (max. 8 MB). Bitte komprimiere es zuerst.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => onUpload(reader.result);
    reader.readAsDataURL(file);
  };
  return (
    <div className="rounded-2xl bg-white border border-ink-700/10 p-4">
      <div className="text-xs uppercase tracking-widest text-ink-500 mb-3">{label}</div>
      {imageData ? (
        <div className="relative">
          <img src={imageData} alt={label} className="w-full h-48 object-contain rounded-lg bg-ivory-100" />
          <button
            onClick={onClear}
            data-testid={`${testId}-clear`}
            className="absolute top-2 right-2 rounded-full bg-white/90 p-1.5 hover:bg-white shadow"
          >
            <X className="w-4 h-4 text-ink-700" />
          </button>
        </div>
      ) : (
        <label
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            handleFile(e.dataTransfer.files?.[0]);
          }}
          className="flex flex-col items-center justify-center gap-2 h-48 border-2 border-dashed border-ink-700/20 rounded-lg cursor-pointer hover:border-gold-500 hover:bg-ivory-100 transition"
        >
          <Upload className="w-8 h-8 text-ink-500" />
          <span className="text-sm text-ink-700">Bild wählen oder hierher ziehen</span>
          <span className="text-xs text-ink-500">JPG, PNG · max. 8 MB</span>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            data-testid={`${testId}-input`}
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
        </label>
      )}
    </div>
  );
};

const IdCertify = () => {
  const { user, authHeader } = useAuth();
  const [docType, setDocType] = useState("id"); // "id" | "pass"
  const [imageFront, setImageFront] = useState("");
  const [imageBack, setImageBack] = useState("");
  const [vermittlerName, setVermittlerName] = useState(user?.name || "");
  const [ort, setOrt] = useState("");
  const [datum, setDatum] = useState(() => new Date().toLocaleDateString("de-CH"));
  const [kundeName, setKundeName] = useState("");
  const [kundeEmail, setKundeEmail] = useState("");
  const [status, setStatus] = useState({ state: "idle", downloadUrl: "", error: "" });
  const [hasSig, setHasSig] = useState(false);
  const sigRef = useRef(null);

  const canSubmit =
    !!imageFront &&
    (docType === "pass" || !!imageBack) &&
    vermittlerName.trim() &&
    ort.trim() &&
    datum.trim() &&
    hasSig;

  const submit = async () => {
    if (!canSubmit) return;
    setStatus({ state: "loading", downloadUrl: "", error: "" });
    try {
      const sigData = sigRef.current.getCanvas().toDataURL("image/png");
      const res = await axios.post(`${API}/id/certify`, {
        doc_type: docType,
        image_front: imageFront,
        image_back: docType === "id" ? imageBack : "",
        vermittler_name: vermittlerName.trim(),
        ort: ort.trim(),
        datum: datum.trim(),
        signature: sigData,
        kunde_name: kundeName.trim(),
        kunde_email: kundeEmail.trim(),
      }, { headers: authHeader() });
      setStatus({
        state: "success",
        downloadUrl: (process.env.REACT_APP_BACKEND_URL || "") + res.data.download_url,
        error: "",
        emailSent: res.data.email_sent,
      });
    } catch (e) {
      setStatus({ state: "error", downloadUrl: "", error: e?.response?.data?.detail || e.message });
    }
  };

  const reset = () => {
    setImageFront(""); setImageBack(""); setKundeName(""); setKundeEmail("");
    sigRef.current?.clear();
    setHasSig(false);
    setStatus({ state: "idle", downloadUrl: "", error: "" });
  };

  return (
    <div className="min-h-screen bg-ivory-50 text-ink-900">
      <Header />
      <main className="max-w-4xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="text-xs uppercase tracking-[0.2em] text-gold-700 mb-3">Ausweisbeglaubigung</div>
          <h1 className="font-display text-4xl md:text-5xl font-medium leading-tight mb-4">
            Kopie mit Original beglaubigen
          </h1>
          <p className="text-ink-700 max-w-2xl mb-8">
            Lade Vorder- und Rückseite der Identitätskarte hoch (oder eine einzelne Seite des Reisepasses).
            Wir erstellen daraus ein sauberes PDF mit deiner Bestätigung und Unterschrift.
          </p>

          {status.state !== "success" ? (
            <>
              {/* Document type */}
              <div className="mb-6 flex gap-3">
                {["id", "pass"].map((t) => (
                  <button
                    key={t}
                    onClick={() => { setDocType(t); if (t === "pass") setImageBack(""); }}
                    data-testid={`doc-type-${t}`}
                    className={`px-5 py-2.5 rounded-full text-sm font-medium transition ${
                      docType === t
                        ? "bg-ink-900 text-gold-300"
                        : "bg-white border border-ink-700/10 text-ink-700 hover:border-gold-500"
                    }`}
                  >
                    {t === "id" ? "Identitätskarte" : "Reisepass"}
                  </button>
                ))}
              </div>

              {/* Image uploads */}
              <div className={`grid ${docType === "id" ? "md:grid-cols-2" : "md:grid-cols-1"} gap-4 mb-8`}>
                <ImageBox
                  label={docType === "id" ? "Vorderseite" : "Reisepass-Seite"}
                  imageData={imageFront}
                  onUpload={setImageFront}
                  onClear={() => setImageFront("")}
                  testId="img-front"
                />
                {docType === "id" && (
                  <ImageBox
                    label="Rückseite"
                    imageData={imageBack}
                    onUpload={setImageBack}
                    onClear={() => setImageBack("")}
                    testId="img-back"
                  />
                )}
              </div>

              {/* Meta fields */}
              <div className="rounded-2xl bg-white border border-ink-700/10 p-5 mb-6">
                <div className="text-sm font-semibold mb-1">Angaben zur Beglaubigung</div>
                {user && (
                  <div className="text-xs text-ink-500 mb-4 flex items-start gap-1.5">
                    <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-gold-600" />
                    <span>Das fertige PDF wird an deine E-Mail (<strong>{user.email}</strong>) versandt. Optional kannst du unten die Kunden-E-Mail als CC hinzufügen.</span>
                  </div>
                )}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-ink-500">Vermittler / Geschäftspartner *</label>
                    <input className={input} placeholder="Vor- und Nachname" value={vermittlerName} onChange={(e) => setVermittlerName(e.target.value)} data-testid="input-vermittler-name" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-ink-500">Kunde/in (optional)</label>
                    <input className={input} placeholder="Vor- und Nachname" value={kundeName} onChange={(e) => setKundeName(e.target.value)} data-testid="input-kunde-name" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-ink-500">Ort *</label>
                    <input className={input} placeholder="z. B. Schmitten" value={ort} onChange={(e) => setOrt(e.target.value)} data-testid="input-ort" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-ink-500">Datum *</label>
                    <input className={input} placeholder="TT.MM.JJJJ" value={datum} onChange={(e) => setDatum(e.target.value)} data-testid="input-datum" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-xs uppercase tracking-widest text-ink-500">E-Mail Kunde/in (optional CC)</label>
                    <input type="email" className={input} placeholder="beispiel@mail.ch – leer lassen, wenn du das PDF nur selbst erhalten willst" value={kundeEmail} onChange={(e) => setKundeEmail(e.target.value)} data-testid="input-kunde-email" />
                  </div>
                </div>
              </div>

              {/* Signature */}
              <div className="rounded-2xl bg-white border border-ink-700/10 p-5 mb-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-sm font-semibold">Unterschrift Vermittler *</div>
                  <button
                    onClick={() => { sigRef.current?.clear(); setHasSig(false); }}
                    data-testid="sig-clear"
                    className="text-xs text-ink-500 hover:text-gold-700 inline-flex items-center gap-1"
                  >
                    <RefreshCw className="w-3 h-3" /> Neu
                  </button>
                </div>
                <div className="rounded-lg bg-ivory-100 border border-ink-700/10">
                  <SignatureCanvas
                    ref={sigRef}
                    penColor="#0F0F0F"
                    onEnd={() => setHasSig(true)}
                    canvasProps={{ className: "w-full h-40 rounded-lg", "data-testid": "sig-canvas" }}
                  />
                </div>
              </div>

              {status.state === "error" && (
                <div className="mb-4 rounded-xl bg-red-50 border border-red-200 p-4 text-sm text-red-700">
                  {status.error || "Fehler beim Erstellen"}
                </div>
              )}

              <div className="flex items-center gap-4">
                <button
                  onClick={submit}
                  disabled={!canSubmit || status.state === "loading"}
                  data-testid="submit-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-ink-900 hover:bg-ink-700 disabled:bg-ink-500 disabled:cursor-not-allowed text-gold-300 px-7 py-3 text-sm font-medium transition"
                >
                  {status.state === "loading" ? "Erstelle PDF …" : "PDF erstellen"}
                  <ArrowRight className="w-4 h-4" />
                </button>
                <div className="text-xs text-ink-500 flex items-center gap-1.5">
                  <Info className="w-3.5 h-3.5" /> Wird nach Erstellung als PDF zum Download bereitgestellt.
                </div>
              </div>
            </>
          ) : (
            <div className="rounded-2xl bg-white border border-ink-700/10 p-8 text-center">
              <CheckCircle2 className="w-14 h-14 mx-auto mb-4 text-gold-600" />
              <div className="text-2xl font-display font-medium mb-2">Beglaubigung erstellt</div>
              <div className="text-sm text-ink-500 mb-6">
                {status.emailSent
                  ? "Das PDF wurde per E-Mail versandt und steht hier zum Download bereit."
                  : "Das PDF steht hier zum Download bereit."}
              </div>
              <a
                href={status.downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="download-btn"
                className="inline-flex items-center gap-2 rounded-full bg-ink-900 hover:bg-ink-700 text-gold-300 px-7 py-3 text-sm font-medium transition"
              >
                <Download className="w-4 h-4" /> PDF herunterladen
              </a>
              <div className="mt-6">
                <button onClick={reset} data-testid="new-cert-btn" className="text-sm text-ink-500 hover:text-gold-700 underline">
                  Neue Beglaubigung erstellen
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
};

export default IdCertify;
